import { valueConsistent } from "./candidateSelection";
import { buildUndoPayload } from "./standardizationDiff";

// Fields the multi-unit candidate fills from (locality). addr1 is handled
// separately: bare-building canonicalizes the street to the candidate's bare
// street_line and drops any secondary.
const LOCALITY_FIELDS = ["city", "state", "zip", "country"];

/**
 * Compute the commit for the **bare-building unverifiable** case (sibling of
 * base-retain): the user has a multi-unit building's bare street but
 * no unit. addr1 becomes the canonical bare street (candidate.streetLine — NO
 * secondary, no "___" sentinel); locality fills from the candidate, EXCEPT a
 * field the user edited to a value the candidate would contradict (kept as
 * typed, same rule as the forward path). The verdict settles unverifiable — a
 * bare multi-unit building isn't a Smarty leaf (the backend rejects it as a
 * USPSPartialMatch, missing the required secondary).
 *
 * Shared by the inline hook resolve and the hold-surface (AddressInput /
 * AddressField) Save handlers so canonicalization + the street-keyed
 * standardization decision are identical everywhere — exactly like
 * buildBaseRetainCommit.
 *
 * @param {object} candidate - the multi-unit top candidate (carries streetLine + locality)
 * @param {string} typedAddr1 - the addr1 the user typed (the bare street, their spelling)
 * @param {object} currentValue - the form's current subfields (for the typed locality)
 * @param {object} editedFields - which subfields the user typed into (city/state/zip)
 * @returns {{ fill: object, streetStandardized: boolean, undoPayload: object }}
 */
export default function buildBareBuildingCommit(
  candidate,
  typedAddr1,
  currentValue = {},
  editedFields = {}
) {
  const canonicalStreet = (candidate.streetLine || "").trim();
  // Locality from the candidate, but PRESERVE a value the user typed that the
  // candidate would overwrite — the same rule the forward path enforces via
  // candidateConsistentWithForm (and identical to buildBaseRetainCommit). Fill
  // from the candidate when the field is empty, consistent, or stale (un-edited);
  // keep the typed value only when the user EDITED it AND it contradicts the
  // candidate. Never blank a field the candidate doesn't carry.
  const fill = { addr1: canonicalStreet };
  LOCALITY_FIELDS.forEach((fieldName) => {
    const candidateFieldValue = candidate[fieldName];
    if (candidateFieldValue === undefined || candidateFieldValue === "") return;
    const typedValue = currentValue[fieldName];
    if (
      editedFields[fieldName] &&
      !valueConsistent(typedValue, candidateFieldValue)
    ) {
      fill[fieldName] = typedValue;
      return;
    }
    fill[fieldName] = candidateFieldValue;
  });
  // Keyed on the STREET only: did canonicalizing the bare street change the
  // tokens the user typed? Auto-filled locality is never standardization.
  const streetStandardized =
    (typedAddr1 || "").trim().toLowerCase() !== canonicalStreet.toLowerCase();
  // Undo restores the user's original typed addr1, keeping the candidate's
  // locality for fields the user never typed (so undo doesn't blank an
  // auto-filled city/state/zip).
  const undoPayload = buildUndoPayload({ addr1: typedAddr1 }, fill);
  return { fill, streetStandardized, undoPayload };
}
